// src/components/account/SidebarNav.tsx

import React from "react";
import { useAuth } from "../../../context/AuthContext";

interface SidebarNavProps {
  activeTab: "profile" | "listings" | "reviews" | "settings";
  setActiveTab: (
    tab: "profile" | "listings" | "reviews" | "settings"
  ) => void;
}

const SidebarNav: React.FC<SidebarNavProps> = ({
  activeTab,
  setActiveTab,
}) => {
  const { user, logout } = useAuth();

  /** Safely get the user's display name from meta fields */
  const getSafeName = () => {
    if (!user) return "User";

    // Prefer 'full_name' from WP user meta
    if (user.meta?.full_name && user.meta.full_name.trim() !== "") {
      return user.meta.full_name;
    }

    // Fallback: combine first_name + last_name if available
    const firstname = user.first_name ?? "";
    const lastname = user.last_name ?? "";
    const full = `${firstname} ${lastname}`.trim();
    if (full !== "") return full;

    // Fallback: username
    return user.username ?? "User";
  };

  const safeName = getSafeName();

  /** Generate initials safely */
  const getInitials = (str: string) => {
    if (!str || typeof str !== "string") return "";
    return str
      .split(" ")
      .filter(Boolean)
      .map((n) => n[0]?.toUpperCase())
      .join("");
  };

  const navItems = [
    { key: "profile", icon: "ri-user-line", label: "Profile" },
    { key: "listings", icon: "ri-building-line", label: "My Listings" },
    { key: "reviews", icon: "ri-star-line", label: "Reviews" },
    { key: "settings", icon: "ri-settings-3-line", label: "Settings" },
  ];

  return (
    <div className="lg:col-span-1">
      <div className="bg-white rounded-2xl shadow-sm p-6 sticky top-8">

        {/* PROFILE HEADER */}
        <div className="flex flex-col items-center mb-6 pb-6 border-b border-gray-200">
          <div className="w-24 h-24 bg-gradient-to-br from-teal-500 to-teal-600 rounded-full flex items-center justify-center mb-4">
            <span className="text-3xl font-bold text-white">
              {getInitials(safeName)}
            </span>
          </div>

          <h3 className="text-lg font-semibold text-gray-900">
            {safeName}
          </h3>

          <p className="text-sm text-gray-500">
            {user?.roles?.includes("provider") ? "Service Provider" : "Patient"}
          </p>
        </div>

        {/* NAVIGATION */}
        <nav className="space-y-2">
          {navItems.map((item) => (
            <button
              key={item.key}
              onClick={() => setActiveTab(item.key as any)}
              className={`w-full flex items-center px-4 py-3 rounded-lg transition-colors cursor-pointer whitespace-nowrap ${
                activeTab === item.key
                  ? "bg-teal-50 text-teal-700"
                  : "text-gray-700 hover:bg-gray-50"
              }`}
            >
              <i className={`${item.icon} text-xl mr-3`}></i>
              {item.label}
            </button>
          ))}
        </nav>

        {/* SIGN OUT */}
        <div className="mt-6 pt-6 border-t border-gray-200">
          <button
            onClick={logout}
            className="w-full flex items-center justify-center px-4 py-3 text-red-600 hover:bg-red-50 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-logout-box-line text-xl mr-2"></i>
            Sign Out
          </button>
        </div>

      </div>
    </div>
  );
};

export default SidebarNav;
